import React, { useEffect, useState, useCallback, Fragment } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { NewsAPI } from '../../../api';
import Typography from '@material-ui/core/Typography';
import ReactHtmlParser from 'react-html-parser';
import DOMPurify from 'dompurify';
import ExpansionPanel from '@material-ui/core/ExpansionPanel';
import ExpansionPanelSummary from '@material-ui/core/ExpansionPanelSummary';
import ExpansionPanelDetails from '@material-ui/core/ExpansionPanelDetails';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import Pagination from '@material-ui/lab/Pagination';
import * as QueryString from 'query-string';
import { useHistory, useLocation } from 'react-router-dom';
import { prettyLocalDate } from '../../../lib/time';
import Button from '../../../components/Button';
import Loader from '../../../components/PulseLoader';
import * as track from '../../../lib/track';

const LIMIT = 30;

const useStyles = makeStyles(theme => ({
  root: {
    width: '100%',
    '& .MuiExpansionPanel-root': {
      borderRadius: '4px',
      border: `1px solid ${theme.palette.common.silver}`,
    },
    '& .MuiExpansionPanelSummary-root': {
      backgroundColor: 'transparent',
      borderLeft: '6px solid transparent',
      '&:hover': {
        backgroundColor:theme.palette.common.paleGrey,
        borderLeft: `6px solid ${theme.palette.secondary.main}`,
      },
    },
    '& .MuiExpansionPanelSummary-root.Mui-expanded': {
      backgroundColor: theme.palette.common.paleGrey,
    },
    '& .MuiIconButton-label': {
      color: theme.palette.primary.main,
      fontSize: '1.7rem',
      fontWeight: 600,
    },
    '& a': {
      color: theme.palette.primary.main,
    }
  },
  content: {
    width: '100%',
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
    flexWrap: 'wrap',
  },
  link: {
    fontWeight: 400,
    marginTop: '1rem',
    padding: 0,
    '&:hover': {
      backgroundColor: 'transparent',
      textDecoration: 'underline',
    },
  },
  pagination: {
    padding: '1rem 0',
  },
}));

const NewsFeed = () => {
  const classes = useStyles();
  const history = useHistory();
  const location = useLocation();
  const urlParams = QueryString.parse(location.search);
  const page = ('page' in urlParams) ? parseInt(urlParams.page) : 1;
  const offset = (page * LIMIT) - LIMIT;
  const [feed, setFeed] = useState([]);
  const [pageCount, setPageCount] = useState(0);
  const [loading, setLoading] = useState(false);

  const getFeed = useCallback(async() => {
    setLoading(true);
    const { payload, total } = await NewsAPI.getFeed(null, LIMIT, offset);
    setPageCount(Math.ceil(total / LIMIT));
    setFeed(payload);
    setLoading(false);
  }, [offset]);

  const onPageChange = (event, page) => {
    history.push(`/profile/news?page=${page}`);
  };

  const clickItem = () => {
    track.sendEvent('open news item', 'click', {});
  };

  const toLink = link => {
    track.sendEvent('open news item external link', 'click', {});
    window.open(link, '_blank');
  };

  useEffect(() => {
    getFeed();
    window.scrollTo(0, 0);
  }, [location, getFeed]);

  if (loading) {
    return <Loader />;
  }

  return (
    <div className={classes.root}>
      {feed.map(entry => (
        <ExpansionPanel key={entry.id} onClick={clickItem}>
          <ExpansionPanelSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls={`panel-${entry.id}-content`}
            id={`panel-${entry.id}-header`}
          >
            <div className={classes.panelContent}>
              <Typography variant="h3" color="primary" gutterBottom>
                {entry.title}
              </Typography>
              <Typography variant="body1">
                {entry.source} | {prettyLocalDate(entry.published, 'LL')}
              </Typography>
            </div>
          </ExpansionPanelSummary>
          <ExpansionPanelDetails>
            <Fragment>
              <div className={classes.content}>
                {'content' in entry.entry ? (
                  ReactHtmlParser(DOMPurify.sanitize(entry.entry.content[0].value))
                ) : (
                  ReactHtmlParser(DOMPurify.sanitize(entry.entry.summary))
                )}
              </div>
              <Button
                label="Open original article in new tab"
                onClick={()=>toLink(entry.link)}
                color="primary"
                variant="text"
                pointer="right"
                className={classes.link}
              />
            </Fragment>
          </ExpansionPanelDetails>
        </ExpansionPanel>
      ))}
      {feed.length > 1 ? (
        <div className={classes.pagination}>
          <Pagination
            count={pageCount}
            page={page}
            variant="outlined"
            shape="rounded"
            onChange={onPageChange}
          />
        </div>
      ) : null}
    </div>
  );
};

export default NewsFeed;
